import React, { useEffect, useState } from 'react';
import {
  useTheme,
  // useScrollTrigger,
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Menu,
  MenuItem,
  Button,
  Avatar,
} from '@mui/material';

import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MoreIcon from '@mui/icons-material/MoreVert';
import MenuIcon from '@mui/icons-material/Menu';

import {
  SET_AUTH_USER,
  USER_FETCH,
  USER_LOGOUT,
} from '../../providers/actionTypes';
import { useUser, useUserDispatch } from '../../providers/UserContext';
import { useAuth, useAuthDispatch } from '../../providers/AuthContext';
import getUserIdentity from '@/util/getUserIdentity';
// import { RefreshSession } from '../RefreshSession';

// function ElevationScroll(props) {
//   const { children, window } = props;
//   const trigger = useScrollTrigger({
//     disableHysteresis: true,
//     threshold: 15,
//     target: window ? window() : undefined,
//   });
//   return React.cloneElement(children, {
//     elevation: trigger ? 4 : 1,
//   });
// }

interface HeaderProps {
  mainSidebarOpen: boolean;
  toggleMainSidebar: () => void;
  colorInvert?: boolean;
  window?: () => Window | undefined;
}

const Header: React.FC<HeaderProps> = ({
  toggleMainSidebar,
  colorInvert = false,
}) => {
  const theme = useTheme();
  const user = useUser();
  const userDispatch = useUserDispatch();
  const auth = useAuth();
  const auth_dispatch = useAuthDispatch();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    useState<null | HTMLElement>(null);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if (auth.user_manager && auth.user === undefined) {
      auth.user_manager
        .getUser()
        .then((auth_user) => {
          if (
            auth_user &&
            auth_user.expires_at &&
            auth_user.expires_at < Date.now() / 1000
          ) {
            // Token is expired, try to refresh it first
            console.log('Token expired in Header, attempting refresh...');
            if (auth.user_manager) {
              auth.user_manager
                .signinSilent()
                .then((refreshedUser) => {
                  if (refreshedUser && refreshedUser.access_token) {
                    // Token refreshed successfully
                    auth_dispatch({
                      type: SET_AUTH_USER,
                      payload: refreshedUser,
                    });
                  } else {
                    // Refresh failed, logout and redirect to login
                    auth_dispatch({ type: USER_LOGOUT });
                    if (auth.user_manager) {
                      auth.user_manager
                        .removeUser()
                        .then(() => {
                          if (auth.user_manager) {
                            auth.user_manager.signinRedirect().then();
                          }
                        })
                        .catch((error) =>
                          console.log('auth remove user failed', error),
                        );
                    }
                  }
                })
                .catch((error) => {
                  console.log('Token refresh failed in Header:', error);
                  // Refresh failed, logout and redirect to login
                  auth_dispatch({ type: USER_LOGOUT });
                  if (auth.user_manager) {
                    auth.user_manager
                      .removeUser()
                      .then(() => {
                        if (auth.user_manager) {
                          auth.user_manager.signinRedirect().then();
                        }
                      })
                      .catch((removeError) =>
                        console.log('auth remove user failed', removeError),
                      );
                  }
                });
            }
          } else {
            auth_dispatch({ type: SET_AUTH_USER, payload: auth_user });
          }
        })
        .catch((error) => {
          console.log('auth get user failed', error);
          auth_dispatch({ type: SET_AUTH_USER, payload: null });
        });
    }
  }, [auth_dispatch, auth.user_manager, auth.user]);

  useEffect(() => {
    if (auth.user) {
      userDispatch({ type: USER_FETCH, payload: getUserIdentity(auth.user) });
    }
  }, [auth.user, userDispatch]);

  const { mode } = theme.palette;

  const logout = async () => {
    if (auth.user_manager) {
      await auth.user_manager.signoutRedirect();
    }
  };

  const menuId = 'primary-navbar-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem
        onClick={() => {
          logout();
          handleMenuClose();
        }}
      >
        <IconButton size='large' color='inherit'>
          <LogoutOutlinedIcon />
        </IconButton>
        <p>Logout</p>
      </MenuItem>
    </Menu>
  );

  const mobileMenuId = 'primary-navbar-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      {/* <MenuItem>
        <IconButton size='large' color='inherit'>
          <Badge color='error'>
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <p>Notifications</p>
      </MenuItem> */}
      <MenuItem
        onClick={() => {
          logout();
          handleMobileMenuClose();
        }}
      >
        <IconButton size='large' color='inherit'>
          <LogoutOutlinedIcon />
        </IconButton>
        <p>Logout</p>
      </MenuItem>
    </Menu>
  );

  return (
    <>
      {/* <ElevationScroll window={window}> */}
      <AppBar
        position={'sticky'}
        sx={{
          top: 0,
          display: 'flex',
        }}
      >
        <Toolbar sx={{ backgroundColor: theme.palette.secondary.main }}>
          <IconButton
            size='large'
            edge='start'
            color='primary'
            onClick={toggleMainSidebar}
            aria-label='menu'
            sx={{
              mr: 1,
            }}
          >
            <MenuIcon fontSize='large' />
          </IconButton>
          <Box
            component='a'
            href='/'
            title='AccQSure'
            width={175}
            sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}
          >
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
              width={1}
            >
              <Box
                component={'img'}
                src={
                  mode === 'light' && !colorInvert ? '/logo.png' : '/logo.png'
                }
                height={1}
                width={1}
              />
            </Box>
          </Box>
          <Box
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              justifyContent: 'center',
            }}
          >
            <Box component='a' href='/' title='AccQSure' width={175}>
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                alignItems={'center'}
                width={1}
              >
                <Box
                  component={'img'}
                  src={
                    mode === 'light' && !colorInvert ? '/logo.png' : '/logo.png'
                  }
                  height={1}
                  width={1}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1 }} />
          {auth.user ? (
            <>
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <IconButton
                  size='large'
                  edge='end'
                  aria-label='account of current user'
                  aria-controls={menuId}
                  aria-haspopup='true'
                  onClick={handleProfileMenuOpen}
                  // color='primary'
                >
                  <Avatar
                    // sx={{
                    //   bgcolor: theme.palette.primary.main,
                    // }}
                    alt={user?.name || ''}
                    src={'/bogus.jpg'}
                  />
                </IconButton>
              </Box>
              <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                  size='large'
                  aria-label='show more'
                  aria-controls={mobileMenuId}
                  aria-haspopup='true'
                  onClick={handleMobileMenuOpen}
                  color='primary'
                >
                  <MoreIcon fontSize='large' />
                </IconButton>
              </Box>
            </>
          ) : (
            <Button
              onClick={() => {
                if (auth.user_manager) {
                  auth.user_manager.signinRedirect();
                }
              }}
              variant='contained'
              color='primary'
              size='large'
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>
      {/* </ElevationScroll> */}
      {renderMobileMenu}
      {renderMenu}
      {/* {auth.user ? <RefreshSession /> : undefined} */}
    </>
  );
};

export default Header;
